/**
 * Organization-sponsored access — the web client for joining.
 *
 * Joining happens HERE, on the web, and only here. Apple rejected a previous
 * baba build that unlocked Pro from a code typed into the app (App Store 3.1.1);
 * guideline 3.1.3(b) permits an app to surface access "acquired … on your web
 * site". This module is that acquisition. It must never be called from a
 * WebView embedded in either app.
 */
import { BABA_API_BASE_URL } from './baba-api'
import { getAuthToken } from './baba-auth'

export interface OrgPublicInfo {
  name: string
  slug: string
  status: 'active' | 'paused' | 'archived' | 'unavailable'
  code?: string
  tier?: 'pro' | 'pro_plus'
  accessSummary?: string
}

export interface OrgJoinSuccess {
  organization: { name: string; slug: string }
  tier: 'pro' | 'pro_plus'
  accessUntil: string
  alreadyMember: boolean
}

/**
 * One message per server error code. The server sends a code precisely so the
 * member-facing wording lives on the client and can be translated; never render
 * a raw `error` string, which is the code itself for join failures.
 */
export const ORG_JOIN_MESSAGES: Record<string, string> = {
  invalid_code: "We don't recognize that code. Check it with your organization.",
  offer_paused: "This program isn't accepting new members right now.",
  offer_expired: "This program isn't accepting new members right now.",
  offer_full: 'All the spots for this program have been used.',
  already_member_elsewhere: 'You already have access through another organization.',
  already_subscribed: 'You already have baba Pro — nothing to add.',
  rate_limited: 'Too many tries. Wait a bit and try again.',
  unauthorized: 'Sign in first, then enter your code.',
  org_access_unavailable: 'Something went wrong on our side. Nothing was changed — try again in a minute.',
}

export function orgJoinMessage(code: string | undefined): string {
  return (code && ORG_JOIN_MESSAGES[code]) || ORG_JOIN_MESSAGES.org_access_unavailable
}

/** `code` is the one in the member's link, so the page describes that code's window. */
export async function fetchOrgPublicInfo(slug: string, code?: string | null): Promise<OrgPublicInfo | null> {
  const query = code ? `?c=${encodeURIComponent(code)}` : ''
  const res = await fetch(`${BABA_API_BASE_URL}/api/extension/orgs/public/${encodeURIComponent(slug)}${query}`)
  if (res.status === 404) return null
  if (!res.ok) return { name: '', slug, status: 'unavailable' }
  return (await res.json()) as OrgPublicInfo
}

export type OrgJoinResult =
  | { ok: true; data: OrgJoinSuccess }
  | { ok: false; code: string }

export async function joinOrganization(code: string): Promise<OrgJoinResult> {
  const token = getAuthToken()
  if (!token) return { ok: false, code: 'unauthorized' }

  const res = await fetch(`${BABA_API_BASE_URL}/api/extension/orgs/join`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ code }),
  })
  const body = (await res.json().catch(() => null)) as (OrgJoinSuccess & { code?: string }) | null
  if (!res.ok) return { ok: false, code: body?.code ?? 'org_access_unavailable' }
  return { ok: true, data: body as OrgJoinSuccess }
}

/**
 * Where a signed-out member was, parked across sign-in. Google and the emailed
 * sign-in link both land on "/", so without this a member who chose either one
 * lost the join page and the code in it. Same shape as the email claim's
 * pending token (components/email/EmailGate.tsx): sessionStorage, resumed by
 * the app shell once signed in.
 */
const PENDING_JOIN_KEY = 'baba.org.pending_join'

/** Only ever a join page on this site: this value becomes a navigation. */
function isJoinPath(path: string): boolean {
  return /^\/join\/[^/?#]+(\?[^#]*)?$/.test(path)
}

export function parkPendingJoin(slug: string, code: string): void {
  const trimmed = code.trim()
  const path = `/join/${encodeURIComponent(slug)}${trimmed ? `?c=${encodeURIComponent(trimmed)}` : ''}`
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(PENDING_JOIN_KEY, path)
  } catch {}
}

export function clearPendingJoin(): void {
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(PENDING_JOIN_KEY)
  } catch {}
}

/**
 * The parked join page. Left in place: JoinPage clears it once it is on screen
 * signed in, so a redirect that is overtaken (the "/" → /translate redirect
 * runs in the same commit) is simply made again on the next mount.
 */
export function readPendingJoin(): string | null {
  try {
    if (typeof sessionStorage === 'undefined') return null
    const path = sessionStorage.getItem(PENDING_JOIN_KEY)
    if (path && isJoinPath(path)) return path
    if (path) sessionStorage.removeItem(PENDING_JOIN_KEY)
    return null
  } catch {
    return null
  }
}

/** "3 December 2026" — the date a member is told their access runs to. */
export function formatAccessUntil(iso: string): string {
  const ms = Date.parse(iso)
  if (!Number.isFinite(ms)) return ''
  return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
}
